import {
  QuestionExceptionHandling,
  TextQuestionConfig,
} from '@gripp/shared-logic';
import { useEffect } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Text } from 'react-native-paper';
import {
  ControlledCheckboxItem,
  useFormContext,
} from '../../../../../components/forms';
import { Colors } from '../../../../../themes/colors';
import { RoutineAdminInputText } from '../../../components/routineAdminInputText';
import {
  MAX_IDP_MANAGED_FIELDS,
  countIdpManagedQuestions,
} from '../../admin/idpQuestions';
import { QuestionException } from '../shared/questionExceptionHandling';
import {
  RoutineQuestionAdminForm,
  RoutineQuestionAdminFormProps,
} from '../types';
import { themedStyleSheet } from '../../../../../themes/themedStyleSheet';

const i18nPrefix = 'routines.workflow.admin.question';

export const TextQuestionAdminForm: RoutineQuestionAdminForm<
  TextQuestionConfig
> = (props: RoutineQuestionAdminFormProps<TextQuestionConfig>) => {
  const { t } = useTranslation();
  const { getValues, setValue } = useFormContext();
  const label = t(`${i18nPrefix}.multiline`);
  const multilineFieldName = props.indexPrefix
    ? `${props.indexPrefix}.multiline`
    : 'multiline';
  const idpManagedFieldName = props.indexPrefix
    ? `${props.indexPrefix}.idpManaged`
    : 'idpManaged';
  const exceptionFieldName = props.indexPrefix
    ? `${props.indexPrefix}.responseException`
    : 'responseException';
  props.control.register(multilineFieldName, {
    value: props.config.multiline,
  });
  props.control.register(idpManagedFieldName, {
    value: props.config.idpManaged,
  });

  const idpEnabled = !!useWatch({ control: props.control, name: 'idpEnabled' });
  const isManaged = !!useWatch({
    control: props.control,
    name: idpManagedFieldName,
  });
  const idpPrompt = useWatch({
    control: props.control,
    name: props.indexPrefix ? `${props.indexPrefix}.idpPrompt` : 'idpPrompt',
  });
  // Trimmed to mirror the backend planner, which drops a whitespace-only
  // prompt — the hint must show whenever the schema's not-blank test fails.
  const promptMissing = isManaged && !(idpPrompt ?? '').trim();

  // A managed field is hidden from the operator and filled in asynchronously
  // — it can never raise a "missing response" exception, so exception
  // handling is forced off (not merely defaulted) as soon as the field
  // becomes managed. `QuestionException` isn't rendered below in that case,
  // so this is the only place the stored value gets cleared.
  useEffect(() => {
    if (!isManaged) return;
    setValue(exceptionFieldName, QuestionExceptionHandling.Off, {
      shouldDirty: true,
    });
  }, [isManaged]);

  // Front-door mirror of the API enqueue planner's own field cap (see
  // idpQuestions.ts) — read imperatively rather than via a whole-tree watch
  // so toggling this checkbox doesn't force every other question's admin
  // form to re-render on every keystroke anywhere in the routine. The hard
  // guarantee lives in WorkflowAdminSchema's cap test; this only disables
  // checking further fields once the count is already at the limit.
  const managedCount = countIdpManagedQuestions(getValues('pages'));
  const capReached = !isManaged && managedCount >= MAX_IDP_MANAGED_FIELDS;

  return (
    <>
      <ControlledCheckboxItem
        control={props.control}
        name={multilineFieldName}
        label={label}
        position="trailing"
        style={styles.container}
        labelStyle={styles.label}
      />
      {idpEnabled && (
        <>
          <ControlledCheckboxItem
            control={props.control}
            name={idpManagedFieldName}
            label={t(`${i18nPrefix}.idpManaged`)}
            position="trailing"
            style={styles.container}
            labelStyle={styles.label}
            disabled={capReached}
          />
          {capReached && (
            <Text style={styles.capHint}>
              {t(`${i18nPrefix}.idpManagedCapReached`)}
            </Text>
          )}
          {isManaged && (
            <>
              <RoutineAdminInputText
                control={props.control}
                name="idpPrompt"
                i18nPrefix={i18nPrefix}
                indexPrefix={props.indexPrefix}
                textArea={true}
              />
              <Text style={styles.hint}>
                {t(`${i18nPrefix}.idpPromptHint`)}
              </Text>
              {promptMissing && (
                <Text style={styles.errorHint}>
                  {t(`${i18nPrefix}.idpPromptRequired`)}
                </Text>
              )}
            </>
          )}
        </>
      )}
      {!isManaged && <QuestionException {...props} />}
    </>
  );
};

const styles = themedStyleSheet((role) => ({
  container: {
    paddingHorizontal: 0,
  },
  label: {
    fontSize: 16,
    lineHeight: 16,
    color: role.textStrong,
    marginBottom: 10,
    fontWeight: '700',
  },
  hint: {
    fontSize: 14,
    lineHeight: 18,
    color: role.textSecondary,
    marginTop: -12,
    marginBottom: 20,
  },
  errorHint: {
    fontSize: 14,
    lineHeight: 18,
    color: Colors.redText,
    marginTop: -12,
    marginBottom: 20,
  },
  capHint: {
    fontSize: 14,
    lineHeight: 18,
    color: Colors.redText,
    marginTop: -4,
    marginBottom: 20,
  },
}));
